import { Box, Button, Modal, Slide, Typography } from '@mui/material';
import {
  IconWrapper,
  InputField,
  IStyles,
  PrimaryButton,
  pxToRem,
  pxTovW,
} from '@geneo2-web/shared-ui';
import { useEffect, useState } from 'react';

const styles: IStyles = {
  root: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100vw',
    marginY: { xs: pxToRem(10), md: pxToRem(18), lg: pxTovW(20) },
    boxSizing: 'border-box',
  },
  container: {
    display: 'flex',
    justifyContent: 'center',
    gap: { xs: pxToRem(40), md: pxToRem(18), lg: pxTovW(20) },
    flexDirection: { xs: 'column', md: 'row' },
    alignItems: { xs: 'center', md: 'flex-start' },
    width: { xs: '95%', md: 'unset' },
  },
  contentBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(15), md: pxToRem(15), lg: pxTovW(15) },
    backgroundColor: 'white',
    width: { xs: '95%', md: pxToRem(480), lg: pxTovW(537) },
    borderRadius: { xs: pxToRem(10), md: pxToRem(18), lg: pxTovW(20) },
    paddingX: { xs: pxToRem(10), md: pxToRem(24), lg: pxTovW(30) },
    paddingY: { xs: pxToRem(10), md: pxToRem(20), lg: pxTovW(23) },
    boxSizing: 'border-box',
  },
  proceedButton: {
    width: { xs: '50%', md: pxToRem(150), lg: pxTovW(200) },
    height: { xs: pxToRem(35), md: 'unset' },
    fontWeight: 'bold',
    paddingY: { xs: pxToRem(10), md: pxToRem(10), lg: pxTovW(10) },
  },
};

interface IProps {
  popupOpen: boolean;
  popupCloseHandler: () => void;
  submitting?: boolean;
  onSubmit: (remarks: string) => void;
}

/** Shown when a teacher resubmits marks for a group/student that was already graded. */
export const ProjectMarksRemarksModal = (props: IProps) => {
  const { popupOpen, popupCloseHandler, submitting, onSubmit } = props;
  const [remarks, setRemarks] = useState('');

  /** A freshly opened modal must not carry the previous submission's remarks. */
  useEffect(() => {
    if (popupOpen) {
      setRemarks('');
    }
  }, [popupOpen]);

  return (
    <Modal open={popupOpen} onClose={popupCloseHandler}>
      <Slide
        direction="up"
        in={popupOpen}
        mountOnEnter
        unmountOnExit
        timeout={500}
      >
        <Box sx={styles.root}>
          <Box sx={styles.container}>
            <Box sx={styles.contentBox}>
              <Typography variant={'bodyText'} fontWeight="bold" textAlign={'center'}>
                Add Remarks
              </Typography>
              <Typography variant="subText" color={'#9F9F9F'} textAlign={'center'}>
                Marks for this group/student have already been submitted.
                Please enter a reason for changing the marks.
              </Typography>
              <InputField
                variant='outlined'
                multiline
                minRows={4}
                nonCircular
                fullWidth
                placeholder="Enter reason for changing marks"
                value={remarks}
                onChange={(event) => setRemarks(event.target.value)}
              />
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: { xs: 'center', lg: 'flex-end' },
                  gap: { xs: pxToRem(15), md: pxToRem(18), lg: pxTovW(20) },
                }}
              >
                <Button
                  variant="contained"
                  onClick={popupCloseHandler}
                  sx={{
                    width: {
                      xs: '50%',
                      md: pxToRem(150),
                      lg: pxTovW(200),
                    },
                    height: { xs: pxToRem(52), lg: 'unset' },
                    fontFamily: 'Lato',
                    fontWeight: 700,
                    color: '#0AA34F',
                    border: '1px solid #0AA34F',
                    fontSize: { xs: pxToRem(16), md: pxToRem(16), lg: pxTovW(16) },
                    borderRadius: '10px',
                    backgroundColor: '#FFFFFF',
                    '&:hover': {
                      backgroundColor: '#FFFFFF',
                      color: '#0AA34F',
                      border: '1px solid #0AA34F',
                      boxShadow: 'none',
                    },
                  }}
                >
                  Cancel
                </Button>
                <PrimaryButton
                  variant="contained"
                  onClick={() => onSubmit(remarks.trim())}
                  sx={styles.proceedButton}
                  disabled={submitting || remarks.trim() === ''}
                >
                  <Typography variant="cardText" fontWeight={700}>
                    Submit
                  </Typography>
                </PrimaryButton>
              </Box>
            </Box>
            <Button onClick={popupCloseHandler} sx={{ p: '0px' }}>
              <IconWrapper
                name="close-yellow"
                size="md"
                parentFolder="icons"
                type="png"
                customSx={{
                  width: { xs: pxToRem(33), md: pxToRem(39), lg: pxTovW(54) },
                  height: { xs: pxToRem(33), md: pxToRem(39), lg: pxTovW(54) },
                }}
              />
            </Button>
          </Box>
        </Box>
      </Slide>
    </Modal>
  );
};

export default ProjectMarksRemarksModal;
